import { isNullOrUndefined } from '../../shared/utils/helper.utils';

export class MathUtils {
  private static readonly Percentage = 100;

  /**
   * Calculates the percentage based on
   * @param {number} numerator
   * @param {number} denominator
   * @param {boolean} allowGreaterThan100 - if `false` it will return 100 if the percentage is > 100
   */
  static calcPercentage(
    numerator: number,
    denominator: number,
    allowGreaterThan100 = false
  ): number {
    const percentage = Math.round((numerator / denominator) * this.Percentage);
    return allowGreaterThan100 ? percentage : Math.min(percentage, this.Percentage);
  }

  /**
   * Returns a number with its correct ordinal suffix (e.g., 1st, 2nd, 3rd, 4th, etc.)
   * @param n - The number to format
   * @returns A string with the number and its ordinal suffix
   */
  public static getNumberOrdinalSuffix = (n: unknown): string => {
    if (typeof n !== 'number' || isNaN(n)) {
      throw new Error('Invalid input: expected a number.');
    }

    const lastDigit = n % 10;
    const lastTwoDigits = n % 100;

    // Special case for numbers ending in 11, 12, or 13
    if (lastTwoDigits >= 11 && lastTwoDigits <= 13) {
      return `${n}th`;
    }

    // Determine suffix based on the last digit
    switch (lastDigit) {
      case 1:
        return `${n}st`;
      case 2:
        return `${n}nd`;
      case 3:
        return `${n}rd`;
      default:
        return `${n}th`;
    }
  };

  /**
   * Checks if the given value is a valid number.
   * @param {string | number | undefined} value - The value to check.
   * @returns {number | null} - Returns the number if valid, otherwise returns null.
   */
  public static isValidNumber = (value: string | number | undefined): number | null => {
    if (!value) {
      return null;
    }

    if (typeof value === 'number') {
      return value;
    }

    const number = Number(value);
    const validNumber = !isNullOrUndefined(number) && !Number.isNaN(number);

    return validNumber ? number : null;
  };
}
